<template>
  <div style="height: 100%;">

    <l-map ref="map" class="map" id="theMap" :zoom="karte.zoom" :center="karte.center" :options="karte.mapOptions">
      <l-control-zoom position="topright"></l-control-zoom>

      <l-tile-layer :name="karte.selectedTile.name" :url="karte.selectedTile.url"
        :attribution="karte.selectedTile.attribution" layer-type="base" />

      <v-marker-cluster ref="cluster" :options="karte.clusterOptions">
        <l-marker :id="'content-marker-' + content.id" :ref="'content-marker-' + content.id"
          v-for="content in contentsWithLocations" :key="content.id" :lat-lng="content.latLng"
          :icon="getSymbolIcon(content)" @click="highlightThisContent(content)">
        </l-marker>
      </v-marker-cluster>

    </l-map>
  </div>
</template>

<script>
import 'leaflet/dist/leaflet.css';
import { latLng, divIcon, latLngBounds } from 'leaflet';
import { LMap, LTileLayer, LMarker, LPopup, LControlZoom } from "vue2-leaflet";
import { getFieldValues } from '@/utils/helpers';
import { getMarkerIcon } from '@/utils/lupe-helpers';
import MarkerCluster from '@/components/map/markerClusterExtension';
import { MarkerCluster as MarkerClusterClass } from 'leaflet.markercluster';

export default {
  name: 'MultipleMapView',
  components: {
    LTileLayer,
    LMarker,
    LPopup,
    LControlZoom,
    LMap,
    'v-marker-cluster': MarkerCluster,
  },
  props: {
    height: {
      type: String,
      default: '700px',
    },
    contents: {
      type: Array,
      required: true,
      default: [],
    },
  },
  data() {
    return {
      L: window.L,
      karte: {
        modus: false,
        zoom: 12,
        center: latLng(52.419191, 10.768606),
        bounds: this.theBounds,
        mapOptions: {
          zoomSnap: 0.5,
          zoomControl: false,
          touchZoom: true,
          dragging: true,
          doubleClickZoom: true,
          scrollWheelZoom: true,
          keyboard: true,
        },
        clusterOptions: {
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
        },
        selectedTile: {
          url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
          attribution: "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors &copy; <a href=\"https://carto.com/attributions\">CARTO</a>"
        },
      },
    };
  },
  watch: {
    contents: function (val) {
      this.$refs.map.fitBounds(this.markerBounds, {
        padding: [50, 50]
      });
    },
  },
  computed: {
    contentsWithLocations() {
      var locationContents = [];

      if (this.contents != null) {
        for (var i = 0; i < this.contents.length; i++) {
          this.getLatLongForContent(this.contents[i]);
          if (this.contents[i].latLng != null) {
            locationContents.push(this.contents[i]);
          }
        }
      }
      return locationContents;
    },
    markerData() {
      if (this.addressObj != null && Object.prototype.hasOwnProperty.call(this.addressObj, 'latitude') && this.addressObj.latitude != null && Object.prototype.hasOwnProperty.call(this.addressObj, 'longitude') && this.addressObj.longitude != null && Object.prototype.hasOwnProperty.call(this.addressObj, 'street') && this.addressObj.street != null && Object.prototype.hasOwnProperty.call(this.addressObj, 'zipcode') && this.addressObj.zipcode != null && Object.prototype.hasOwnProperty.call(this.addressObj, 'city') && this.addressObj.city != null) {

        var mapMarker = { lat: this.addressObj.latitude, long: this.addressObj.longitude, street: this.addressObj.street, zipcode: this.addressObj.zipcode, city: this.addressObj.city, latLng: latLng(this.addressObj.latitude, this.addressObj.longitude) };
        return mapMarker;
      }
      return null;
    },
    markerBounds() {
      var bounds = [];
      for (var i = 0; i < this.contentsWithLocations.length; i++) {
        var coords = this.contentsWithLocations[i].latLng;
        bounds.push(coords);
      }
      return latLngBounds(bounds);
    }
  },
  mounted() {

  },
  methods: {
    getMarkerIcon,
    getSymbolIcon(content) {
      var icon = getMarkerIcon(content);
      var customIcon = null;
      if (Object.prototype.hasOwnProperty.call(content, 'highlight') && content.highlight == true) {
        customIcon = divIcon(icon);
      }
      else {
        customIcon = divIcon(icon);
      }
      return customIcon;
    },
    getLatLongForContent(content) {
      if (content == null) {
        return;
      }

      var vals = null;
      vals = getFieldValues(content, 'adresse');

      /*check if there is a latitude and a longitude*/
      if (vals != null && Object.prototype.hasOwnProperty.call(vals, 'latitude') && Object.prototype.hasOwnProperty.call(vals, 'latitude')) {
        content.latLng = latLng(vals.latitude, vals.longitude);
        content.lat = vals.latitude;
        content.long = vals.longitude;
        return content.latLng;
      }
      content.lat = null;
      content.long = null;
      content.latLng = null;
      return null;
    },
    highlightThisContent(content) {
      this.$emit('highlight', { sender: 'map', content: content });
    },
    openPopup(content) {
      if ('content-marker-' + content.id in this.$refs) {
        var marker = this.$refs['content-marker-' + content.id][0].mapObject;
        var map = this.$refs.map.mapObject;
        var cluster = this.$refs.cluster.mapObject;
        var currentZoom = map.getZoom();
        var targetZoom = 20;
        var needsSpiderfy = false; // Track if we need to spiderfy

        // Unspiderfy all clusters first, but remember if we'll need to spiderfy
        this.$refs['cluster'].unspiderfyAll();

        // Calculate zoom duration based on zoom difference for smoother animation
        var zoomDifference = Math.abs(targetZoom - currentZoom);
        var zoomDuration = Math.min(0.8, Math.max(0.4, zoomDifference * 0.05));

        // Start zooming with smooth animation
        map.setView(content.latLng, targetZoom, {
          animate: true,
          duration: zoomDuration,
          easeLinearity: 0.25 // Smoother easing
        });

        // Wait for zoom to complete
        map.once('zoomend', () => {
          // Wait for cluster to update after zoom - clusters need time to recalculate
          setTimeout(() => {
            // Get fresh marker reference after zoom
            if ('content-marker-' + content.id in this.$refs) {
              var updatedMarker = this.$refs['content-marker-' + content.id][0].mapObject;

              // Check if marker is visible (not in a cluster)
              var visibleParent = cluster.getVisibleParent(updatedMarker);

              if (!(visibleParent instanceof MarkerClusterClass)) {
                // Marker is visible individually, ensure no other clusters are spiderfied
                this.$refs['cluster'].unspiderfyAll();
                // Small delay before opening popup for smoother transition
                setTimeout(() => {
                  updatedMarker.openPopup();
                }, 150);
              } else {
                // Marker is still in a cluster - markers are very close together
                // We're already at zoom 20, can't zoom in more, so spiderfy immediately
                this.spiderfyAndOpenPopup(updatedMarker, cluster, content.id);
              }
            }
          }, 400);
        });
      }
    },
    spiderfyAndOpenPopup(marker, cluster, contentId) {
      // Get the visible parent (cluster) containing this marker
      var visibleParent = cluster.getVisibleParent(marker);

      if (visibleParent instanceof MarkerClusterClass) {
        var self = this;

        // Set up event listener FIRST (before calling spiderfy)
        // This follows the exact pattern from fireEventOnMarkerOrVisibleParentCluster
        cluster.once('spiderfied', function () {
          // Wait for spiderfy animation to complete
          setTimeout(() => {
            // Try to find and open popup on the marker
            if ('content-marker-' + contentId in self.$refs) {
              var finalMarker = self.$refs['content-marker-' + contentId][0].mapObject;
              finalMarker.openPopup();
            } else {
              // Fallback: try to open popup on the original marker
              marker.openPopup();
            }
          }, 400);
        });

        // Now call spiderfy() synchronously - this automatically unspiderfies other clusters
        // This is the correct pattern as shown in the existing codebase
        visibleParent.spiderfy();
      } else {
        // Not in cluster anymore, just open popup
        setTimeout(() => {
          marker.openPopup();
        }, 100);
      }
    }
  },
};
</script>
<style lang="scss">
@import '@/scss/markers.scss';
@import "~leaflet.markercluster/dist/MarkerCluster.css";
@import "~leaflet.markercluster/dist/MarkerCluster.Default.css";

.leaflet-container .leaflet-marker-pane img {
  -webkit-filter: drop-shadow(3px 3px 4px rgba(0, 0, 0, .5));
  filter: drop-shadow(3px 3px 4px rgba(0, 0, 0, .5));
}

.marker-cluster-medium,
.marker-cluster-medium div,
.marker-cluster-small,
.marker-cluster-small div {
  color: #fff;
  background-color: rgba(46, 85, 165, .6);
}
</style>
